import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PaletteSettings } from '@/features/PaletteSettings/PaletteSettings';
import { panelPath, validPanelId } from '@/entities/Panel/model';
import { RailwayCanvas } from '@/shared/ui/RailwayCanvas/RailwayCanvas';
import { useSeo } from '@/shared/lib/seo/seo';
import { useThemeType } from '@gravity-ui/uikit';
import { Page } from '@/widgets/Page';
import './MainPage.css';
import { PhotoReveal } from './PhotoReveal';

export function MainPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const settings = searchParams.get('tab') === 'settings';
    const theme = useThemeType();
    const [paused, setPaused] = useState(false);
    const [panelId, setPanelId] = useState('');
    const [error, setError] = useState('');

    useSeo({
        title: 'Управление макетом',
        description:
            'Управление локомотивами, станциями и стрелками железнодорожного макета из браузера.',
        canonicalPath: '/',
    });
    return (
        <Page className="MainPage dashboard-page">
            {settings ? <PaletteSettings /> : <>
            <section className="home-hero" aria-labelledby="home-title">
                <div className="home-railway">
                    <RailwayCanvas paused={paused} dark={theme === 'dark'} />
                </div>
                <div className="home-hero-copy">
                    <h1 id="home-title">
                        Всё движение.
                        <br />В ваших руках.
                    </h1>
                    <p>
                        Настоящий макет. Живые маршруты.
                        <br />
                        Один экран для всего, что движется.
                    </p>
                    <a className="hero-link" href="#connect">
                        К управлению <span aria-hidden="true">↗</span>
                    </a>
                </div>
            </section>
            <section id="connect" className="home-access" aria-labelledby="connect-title">
                <div className="home-access-intro">
                    <span className="eyebrow">01 / Ваш участок</span>
                    <h2 id="connect-title">
                        Следующая станция —<br />
                        ваша панель.
                    </h2>
                    <p>
                        Локомотивы, станции и стрелки вашего участка — рядом. Введите ID панели,
                        чтобы начать управление.
                    </p>
                </div>
                <form
                    className="surface panel-connect"
                    onSubmit={(event) => {
                        event.preventDefault();
                        const id = panelId.trim();
                        if (!validPanelId(id)) {
                            setError(
                                'Проверьте ID: допустимы латинские буквы, цифры, дефис и знак подчёркивания.',
                            );
                            return;
                        }
                        navigate(panelPath(id));
                    }}
                >
                    <span className="eyebrow">Ваш участок</span>
                    <h2>Подключиться к панели</h2>
                    <label className="field" htmlFor="panel-id">
                        ID панели
                    </label>
                    <input
                        id="panel-id"
                        name="panelId"
                        className="panel-id-input"
                        placeholder="Введите ID панели"
                        required
                        maxLength={80}
                        autoComplete="off"
                        autoCapitalize="none"
                        spellCheck={false}
                        value={panelId}
                        aria-invalid={Boolean(error)}
                        aria-describedby={error ? 'panel-id-error' : 'panel-id-hint'}
                        onChange={(event) => {
                            setPanelId(event.target.value);
                            setError('');
                        }}
                    />
                    {error && (
                        <p id="panel-id-error" className="notice error" role="alert">
                            {error}
                        </p>
                    )}
                    <button type="submit" className="action primary">
                        Подключиться <span aria-hidden="true">↗</span>
                    </button>
                    <p id="panel-id-hint" className="hint">
                        После подключения потребуется пароль доступа.
                    </p>
                </form>
            </section>
            <section className="project-about" aria-labelledby="about-title">
                <div className="section-heading">
                    <div>
                        <span className="eyebrow">02 / О проекте</span>
                        <h2 id="about-title">Настоящий макет. Цифровое управление.</h2>
                    </div>
                </div>
                <p className="project-description">
                    «Локомотив» — система управления макетом железной дороги. Она связывает
                    физические локомотивы, станции и стрелочные переводы с интерфейсом в браузере.
                    На персональной панели собраны объекты вашего участка и их текущее состояние.
                </p>
                <div className="project-features">
                    <article>
                        <span className="feature-number">01</span>
                        <h3>Движение</h3>
                        <p>
                            Задавайте направление и тяговую позицию локомотивов, следите за их
                            состоянием.
                        </p>
                    </article>
                    <article>
                        <span className="feature-number">02</span>
                        <h3>Станции</h3>
                        <p>
                            Управляйте стрелочными переводами и контролируйте положение стрелок на
                            участке.
                        </p>
                    </article>
                    <article>
                        <span className="feature-number">03</span>
                        <h3>Наблюдение</h3>
                        <p>
                            Камера помогает отслеживать расположение локомотивов и данные движения в
                            админ-панели.
                        </p>
                    </article>
                </div>
            </section>
            <PhotoReveal />
            <footer className="home-footer">
                <span>Макет железной дороги</span>
                <span>Управление в реальном времени</span>
            </footer>
            </>}
        </Page>
    );
}
